
'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import type { SupportLink, UpsertSupportLink } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, Timestamp, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

interface SupportContextType {
    links: SupportLink[];
    loading: boolean;
    createLink: (link: UpsertSupportLink) => Promise<void>;
    updateLink: (linkId: string, linkData: UpsertSupportLink) => Promise<void>;
    deleteLink: (linkId: string) => Promise<void>;
}

const SupportContext = createContext<SupportContextType | undefined>(undefined);

const safeGetDate = (timestamp: any): Date => {
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    return new Date();
};

export const SupportProvider = ({ children }: { children: ReactNode }) => {
    const [links, setLinks] = useState<SupportLink[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();
    const { user, isOwner } = useAuth();

    useEffect(() => {
        const q = query(collection(db, "supportLinks"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const linksData: SupportLink[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                linksData.push({
                    id: doc.id,
                    artistName: data.artistName,
                    postUrl: data.postUrl,
                    imageUrl: data.imageUrl,
                    imagePath: data.imagePath,
                    createdAt: safeGetDate(data.createdAt),
                });
            });
            setLinks(linksData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore snapshot error (supportLinks):", error);
            toast({
                title: "Connection Error",
                description: "Could not load support links.",
                variant: "destructive",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [toast]);
    
    const createLink = async (link: UpsertSupportLink) => {
        if (!user || !isOwner) return;
        if (!link.imageFile) {
            toast({ title: "Image required", description: "Please provide an image for the link.", variant: "destructive" });
            return;
        }

        try {
            const imagePath = `support-links/${uuidv4()}`;
            const storageRef = ref(storage, imagePath);
            await uploadBytes(storageRef, link.imageFile);
            const imageUrl = await getDownloadURL(storageRef);

            const linkToCreate = {
                 artistName: link.artistName,
                 postUrl: link.postUrl,
                 imageUrl,
                 imagePath,
                 createdAt: serverTimestamp(),
            }

            await addDoc(collection(db, 'supportLinks'), linkToCreate);
            toast({ title: "Success", description: "Link added successfully." });
        } catch (error) {
            console.error("Error creating link:", error);
            toast({ title: "Error", description: "Failed to create link.", variant: "destructive" });
        }
    };
    
    const updateLink = async (linkId: string, linkData: UpsertSupportLink) => {
        if (!user || !isOwner) return;

        try {
            const linkRef = doc(db, 'supportLinks', linkId);
            const updatePayload: any = {
                artistName: linkData.artistName,
                postUrl: linkData.postUrl,
            };

            if (linkData.imageFile) {
                 const imagePath = `support-links/${uuidv4()}`;
                 const storageRef = ref(storage, imagePath);
                 await uploadBytes(storageRef, linkData.imageFile);
                 const imageUrl = await getDownloadURL(storageRef);
                 updatePayload.imageUrl = imageUrl;
                 updatePayload.imagePath = imagePath;
                 
                 // Optionally delete old image
                 const oldLink = links.find(l => l.id === linkId);
                 if (oldLink?.imagePath) {
                     try {
                        await deleteObject(ref(storage, oldLink.imagePath));
                     } catch (e) {
                        console.warn("Could not delete old image, it might not exist.", e)
                     }
                 }
            }

            await updateDoc(linkRef, updatePayload);
            toast({ title: "Success", description: "Link updated." });

        } catch (error) {
             console.error("Error updating link:", error);
             toast({ title: "Error", description: "Update failed.", variant: "destructive" });
        }
    };

    const deleteLink = async (linkId: string) => {
        if (!user || !isOwner) return;
        try {
            const linkDocRef = doc(db, 'supportLinks', linkId);
            const linkToDelete = await getDoc(linkDocRef);

            if(linkToDelete.exists() && linkToDelete.data().imagePath) {
                await deleteObject(ref(storage, linkToDelete.data().imagePath));
            }
            await deleteDoc(linkDocRef);
            toast({ title: "Success", description: "Link deleted." });
        } catch(error) {
            console.error("Error deleting link:", error);
            toast({ title: "Error", description: "Deletion failed.", variant: "destructive" });
        }
    };

    return (
        <SupportContext.Provider value={{ links, loading, createLink, updateLink, deleteLink }}>
            {children}
        </SupportContext.Provider>
    );
};

export const useSupport = () => {
    const context = useContext(SupportContext);
    if (context === undefined) {
        throw new Error('useSupport must be used within a SupportProvider');
    }
    return context;
};
